import React from 'react';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { useTestimonialsContent } from '../hooks/useCMSContent';
import { Quote, Users, Clock, TrendingUp } from 'lucide-react';
import AnimatedCounter from './AnimatedCounter';

const defaultTestimonials = [
  { quote: "Our AI chatbot captures leads every weekend that we used to lose. We don't even have to think about it anymore.", author: 'Happy Client', role: 'Business Owner', industry: 'Service Industry' },
  { quote: "They mapped out exactly where AI would help, built it, and had it running in under a month. It handles 40% of our front-desk calls now.", author: 'Satisfied Partner', role: 'Practice Manager', industry: 'Healthcare' },
  { quote: "We captured 6 new leads in the first week — one became a $4,000 job. The ROI was immediate.", author: 'Returning Client', role: 'Business Owner', industry: 'Home Services' }
];

const defaultStats = [
  { value: 3, suffix: 'x', label: 'More leads captured after hours' },
  { value: 40, suffix: '%', label: 'Reduction in repetitive tasks' },
  { value: 30, suffix: ' days', label: 'Average time to live system' }
];

const statIcons = [Users, TrendingUp, Clock];

// Parse string values like "3x", "40%", "30 days" into numeric + suffix
const parseStat = (s) => {
  if (!s) return { value: 0, suffix: '', label: '' };
  if (typeof s.value === 'number') return s; // already parsed
  const match = String(s.value || '').match(/^(\d+(?:\.\d+)?)(.*)$/);
  if (match) return { value: parseFloat(match[1]), suffix: match[2], label: s.label };
  return { value: 0, suffix: String(s.value || ''), label: s.label };
};

const TestimonialsSection = () => {
  const [headerRef, headerVisible] = useScrollAnimation(0.2);
  const { testimonials: cmsData } = useTestimonialsContent();

  const testimonials = cmsData?.items || defaultTestimonials;
  const stats = cmsData?.stats || defaultStats;
  const badge = cmsData?.badge || 'Results';
  const title = cmsData?.title || 'Real results, real businesses.';

  return (
    <section id="testimonials" className="py-20 bg-[#050505] overflow-hidden">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          ref={headerRef}
          initial={{ opacity: 0, y: 40 }}
          animate={headerVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-10"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FF6B00]/10 border border-[#FF6B00]/30 text-[10px] font-mono-tech uppercase tracking-[0.25em] text-[#FF6B00] mb-5 ">
            {badge}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
            {title}
          </h2>
        </motion.div>

        {/* Stats — uniform glass cards with animated counters */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
          {stats.map((rawStat, index) => {
            const stat = parseStat(rawStat);
            const Icon = statIcons[index] || TrendingUp;
            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                animate={headerVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.12 }}
                whileHover={{ y: -4, borderColor: 'rgba(255,107,0,0.4)' }}
                className="relative bg-white/[0.03] backdrop-blur-md rounded-2xl p-6 text-center border border-white/[0.08] overflow-hidden group"
                data-testid={`social-proof-stat-${index}`}
              >
                {/* Orange glow on hover */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
                  background: 'radial-gradient(circle at 50% 0%, rgba(255,107,0,0.18) 0%, transparent 60%)'
                }} />
                <div className="relative z-10">
                  <div className="w-10 h-10 rounded-xl bg-[#FF6B00]/15 border border-[#FF6B00]/25 flex items-center justify-center mx-auto mb-3">
                    <Icon className="w-4 h-4 text-[#FF6B00]" />
                  </div>
                  <p className="text-4xl md:text-5xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                  </p>
                  <p className="text-xs text-[#A1A1AA] mt-2 font-mono-tech uppercase tracking-wider">{stat.label}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Testimonials */}
        <div className="grid md:grid-cols-3 gap-5" data-testid="testimonials-grid">
          {testimonials.map((t, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={headerVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.12 }}
              whileHover={{ y: -6, borderColor: 'rgba(255,107,0,0.3)' }}
              className="relative bg-[#0A0A0A] rounded-2xl p-6 border border-white/[0.08] group overflow-hidden transition-all"
              data-testid={`testimonial-card-${index}`}
            >
              {/* Decorative glow */}
              <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
                background: 'radial-gradient(circle, rgba(255,107,0,0.2) 0%, transparent 70%)'
              }} />
              <div className="relative z-10">
                <Quote className="w-7 h-7 text-[#FF6B00] mb-3" />
                <p className="text-[#A1A1AA] text-sm italic leading-relaxed mb-4">"{t.quote}"</p>
                <div className="border-t border-white/[0.08] pt-3">
                  <p className="font-semibold text-white text-sm">{t.author}</p>
                  <p className="text-xs text-[#71717A] mt-0.5">{t.role} — {t.industry}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
